import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User as FirebaseUser, 
  UserCredential, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail, 
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase/init';
import { checkInvitationStatus, InvitationStatus } from '../lib/services/InvitationService';
import { fetchWhoami } from '../trustgate/data/TrustGateDataService';

// Types
type User = FirebaseUser | null;

type AuthContextType = {
  currentUser: User;
  user: User; // Alias for currentUser for component consistency
  loading: boolean;
  // Invitation status for invite-only gating
  invitationStatus: InvitationStatus | null;
  invitationLoading: boolean;
  isInvited: boolean;
  isAdmin: boolean;
  signup: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  signOut: () => Promise<void>; // Alias for logout
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: { displayName?: string; photoURL?: string }) => Promise<void>;
  refreshInvitationStatus: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  
  // Invitation status for invite-only gating
  const [invitationStatus, setInvitationStatus] = useState<InvitationStatus | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);

  const isTrustGateDomain = typeof window !== 'undefined' && (
    window.location.hostname === 'trustgate.dev' ||
    window.location.hostname === 'www.trustgate.dev' ||
    window.location.hostname === 'trustgate-27ddc.web.app'
  );

  // Refresh invitation status from Firestore
  const refreshInvitationStatus = async () => {
    if (!currentUser) {
      setInvitationStatus(null);
      return;
    }
    
    setInvitationLoading(true);
    try {
      if (isTrustGateDomain) {
        const status = await checkInvitationStatus(currentUser.uid, currentUser.email);

        const idToken = await currentUser.getIdToken();
        const whoami = await fetchWhoami(idToken);
        const isAdmin = whoami?.isAdmin === true;

        console.log('[TrustGate][whoami]', {
          ok: whoami?.ok,
          authenticated: whoami?.authenticated,
          invited: whoami?.invited,
          isAdmin: whoami?.isAdmin,
          env: whoami?.env,
          uid: whoami?.uid,
        });

        setInvitationStatus({
          isInvited: status.isInvited,
          isAdmin,
          role: isAdmin ? 'admin' : (status.isInvited ? 'invited' : null),
          reason: status.isInvited
            ? (isAdmin ? 'Admin (claims)' : 'Invited (no admin claims)')
            : status.reason,
        });
      } else {
        const status = await checkInvitationStatus(currentUser.uid, currentUser.email);
        setInvitationStatus(status);
        console.log(`🎫 Invitation status for ${currentUser.email}: ${status.isInvited ? 'INVITED' : 'NOT INVITED'} (${status.reason})`);
      }
    } catch (error) {
      console.error('Error checking invitation status:', error);
      setInvitationStatus({ isInvited: false, isAdmin: false, role: null, reason: 'Error checking status' });
    } finally {
      setInvitationLoading(false);
    }
  };

  // Check invitation status when user changes
  useEffect(() => {
    if (currentUser && !loading) {
      refreshInvitationStatus();
    } else if (!currentUser) {
      setInvitationStatus(null);
    }
  }, [currentUser, loading]);

  // Sign up function with display name
  const signup = async (email: string, password: string, displayName: string) => {
    if (!auth) throw new Error('Firebase Auth is not initialized');
    const userCredential = await createUserWithEmailAndPassword(auth!, email, password);
    await updateProfile(userCredential.user, { displayName });
    return userCredential;
  };

  // Login function
  const login = (email: string, password: string) => {
    if (!auth) return Promise.reject(new Error('Firebase Auth is not initialized'));
    return signInWithEmailAndPassword(auth!, email, password);
  };

  // Logout function
  const logout = () => {
    if (!auth) return Promise.resolve();
    return signOut(auth!);
  };

  // Reset password function
  const resetPassword = (email: string) => {
    if (!auth) return Promise.reject(new Error('Firebase Auth is not initialized'));
    return sendPasswordResetEmail(auth!, email);
  };

  // Update user profile
  const updateUserProfile = async (data: { displayName?: string; photoURL?: string }) => {
    if (!auth) throw new Error('Firebase Auth is not initialized');
    if (!auth!.currentUser) throw new Error('No user is currently signed in');
    await updateProfile(auth!.currentUser, data);
    setCurrentUser({ ...auth!.currentUser });
  };

  // Set up auth state listener
  useEffect(() => {
    if (!auth) {
      // Fail closed: without Firebase Auth, do not attempt to authenticate.
      setCurrentUser(null);
      setInvitationStatus(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth!, (user) => {
      if (user) {
        setCurrentUser(user);
        setLoading(false);
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    user: currentUser, // Alias for currentUser
    loading,
    // Invitation status for invite-only gating
    invitationStatus,
    invitationLoading,
    isInvited: invitationStatus?.isInvited ?? false,
    isAdmin: invitationStatus?.isAdmin ?? false,
    signup,
    login,
    logout,
    signOut: logout, // Alias for logout for component consistency
    resetPassword,
    updateUserProfile,
    refreshInvitationStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
